import React, { useState } from "react";

import Button from "../Button";
import Modal from "../Modal";

const inputStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink-900)",
  background: "#fff",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  padding: "11px 13px",
  outline: "none",
};

/** Kill-switch modal — required, audited reason. Disabling stops NEW calls only. */
export default function KillSwitchModal({
  orgName,
  toDisable,
  onCancel,
  onConfirm,
}: {
  orgName: string;
  toDisable: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!reason.trim()) {
      setErr("A reason is required (server-enforced).");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(reason.trim());
    } catch (e: any) {
      setErr(e?.message || "Action failed");
      setBusy(false);
    }
  };

  const title = toDisable ? "Disable dialing" : "Enable dialing";
  return (
    <Modal title={title} onClose={onCancel}>
      <p style={{ margin: "0 0 18px", fontSize: 13.5, color: "var(--text-body)", lineHeight: 1.55 }}>
        {toDisable ? "Disable dialing for " : "Re-enable dialing for "}
        <b>{orgName}</b>.{" "}
        {toDisable ? "New calls will be refused for this org." : "The org will be able to place calls again."}
      </p>
      <div style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.55 }}>
        Disabling prevents <b style={{ color: "var(--ink-700)" }}>new</b> calls. A call already in progress is not dropped — suspend the user to end a live session.
      </div>
      <label style={{ display: "block", fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 12.5, color: "var(--ink-700)", marginBottom: 6 }}>
        Reason <span style={{ color: "var(--danger-500)" }}>(required, audited)</span>
      </label>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Nonpayment — invoice overdue"
        style={inputStyle}
        autoFocus
      />
      {err && <p style={{ margin: "6px 0 0", color: "var(--danger-500)", fontSize: 12.5 }}>{err}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={confirm} disabled={busy}>{busy ? "Working…" : title}</Button>
      </div>
    </Modal>
  );
}
